import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const KEY = "hm_cookie_consent_v1";

function readConsent() {
  try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; }
}

export function hasAnalyticsConsent() {
  const c = readConsent();
  return !!c?.analytics;
}

export function hasMarketingConsent() {
  const c = readConsent();
  return !!c?.marketing;
}

/**
 * GDPR-compliant cookie consent banner.
 * - Necessary cookies always on (auth, session, security)
 * - Analytics + Marketing require explicit opt-in (ePrivacy + GDPR Art.6)
 * - Granular controls, accept all / reject all / custom preferences
 * - Dismissable, remembers choice for 12 months
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const c = readConsent();
    if (!c) setVisible(true);
    else {
      setAnalytics(!!c.analytics);
      setMarketing(!!c.marketing);
    }
  }, []);

  const save = (cfg) => {
    const payload = {
      necessary: true,
      analytics: !!cfg.analytics,
      marketing: !!cfg.marketing,
      version: 1,
      decidedAt: new Date().toISOString(),
    };
    localStorage.setItem(KEY, JSON.stringify(payload));
    setVisible(false);
    window.dispatchEvent(new CustomEvent("hm-cookie-consent-changed", { detail: payload }));
  };

  const acceptAll = () => save({ analytics: true, marketing: true });
  const rejectAll = () => save({ analytics: false, marketing: false });
  const saveCustom = () => save({ analytics, marketing });

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] border-t-2 border-[#FFBE00] bg-black/95 backdrop-blur-md"
      role="dialog"
      aria-live="polite"
      aria-label="Informativa cookie"
      data-testid="cookie-banner"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-5">
        {!showDetails ? (
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
            <div className="flex-1 text-sm text-zinc-300">
              <div className="font-heading font-black text-base text-[#FFBE00] mb-1">🍪 Cookie & Privacy</div>
              <p>
                HostMarket usa cookie tecnici <strong>necessari</strong> al funzionamento. Con il tuo consenso usiamo cookie <strong>analitici</strong> per capire come migliorare il sito. Non usiamo cookie pubblicitari di terze parti.{" "}
                <Link to="/cookie" className="underline text-[#FFBE00]">Cookie Policy</Link> ·{" "}
                <Link to="/privacy" className="underline text-[#FFBE00]">Privacy Policy</Link>
              </p>
            </div>
            <div className="flex flex-wrap gap-2 w-full lg:w-auto">
              <button onClick={() => setShowDetails(true)} className="px-4 py-2 text-xs uppercase tracking-wider font-bold border border-zinc-700 rounded-md text-zinc-300 hover:border-white hover:text-white" data-testid="cookie-customize">
                Personalizza
              </button>
              <button onClick={rejectAll} className="px-4 py-2 text-xs uppercase tracking-wider font-bold border border-zinc-700 rounded-md text-zinc-300 hover:border-white hover:text-white" data-testid="cookie-reject-all">
                Solo essenziali
              </button>
              <button onClick={acceptAll} className="px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#FFBE00] text-black rounded-md hover:bg-[#E619A8] hover:text-white transition-colors" data-testid="cookie-accept-all">
                Accetta tutti
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="font-heading font-black text-base text-[#FFBE00] mb-3">Preferenze cookie</div>
            <div className="space-y-2 mb-4">
              <CookieRow label="Essenziali" required description="Autenticazione, sessione, sicurezza. Indispensabili e non disattivabili." />
              <CookieRow label="Analitici" checked={analytics} onChange={setAnalytics} description="Statistiche anonime di navigazione per migliorare il prodotto. Nessun tracking pubblicitario." testid="cookie-toggle-analytics" />
              <CookieRow label="Marketing" checked={marketing} onChange={setMarketing} description="Cookie per campagne pubblicitarie mirate (attualmente non utilizzati da HostMarket)." testid="cookie-toggle-marketing" />
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button onClick={() => setShowDetails(false)} className="px-4 py-2 text-xs uppercase tracking-wider text-zinc-400 hover:text-white">Indietro</button>
              <button onClick={rejectAll} className="px-4 py-2 text-xs uppercase tracking-wider border border-zinc-700 rounded-md">Rifiuta opzionali</button>
              <button onClick={saveCustom} className="px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#FFBE00] text-black rounded-md hover:bg-[#E619A8] hover:text-white" data-testid="cookie-save-custom">Salva preferenze</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CookieRow({ label, description, required, checked, onChange, testid }) {
  return (
    <label className={`flex items-start gap-3 p-3 rounded-md border border-zinc-800 ${required ? "bg-zinc-900/50" : "bg-black/40 cursor-pointer hover:border-zinc-700"}`}>
      <input
        type="checkbox"
        checked={required ? true : !!checked}
        disabled={!!required}
        onChange={(e) => onChange && onChange(e.target.checked)}
        className="mt-1 accent-[#FFBE00]"
        data-testid={testid}
      />
      <div className="flex-1">
        <div className="font-bold text-sm text-white flex items-center gap-2">
          {label}
          {required && <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-[#FFBE00] text-black">Obbligatori</span>}
        </div>
        <div className="text-xs text-zinc-400 mt-0.5">{description}</div>
      </div>
    </label>
  );
}
